import { useEffect, useState } from "react";
import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { Table, type Column } from "@freightsimple/ui-core/Table/Table";
import { TextCell } from "@freightsimple/ui-core/Cells/TextCell";
import { type DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import type { PerformanceLaneDetail } from "@freightsimple/generated-carriers-openapi-client/models/PerformanceLaneDetail";
import type { PerformanceLaneMonthRow } from "@freightsimple/generated-carriers-openapi-client/models/PerformanceLaneMonthRow";
import type { PerformanceLaneRow } from "@freightsimple/generated-carriers-openapi-client/models/PerformanceLaneRow";
import type { PerformanceLaneShipment } from "@freightsimple/generated-carriers-openapi-client/models/PerformanceLaneShipment";
import { carriersApi } from "@/App/Auth/carrierApi";
import { CarrierLoadFailed } from "@/App/CarrierLoadFailed";
import { formatMoney, formatPercent } from "@/App/Views/carrierFormatting";
import { PerformanceMetric } from "@/App/Performance/PerformanceMetric";

interface LaneDrillDownDialogProps extends DialogBaseProps {
  lane: PerformanceLaneRow;
  fromDate: string;
  toDate: string;
}

/**
 * One lane, shipment by shipment.
 *
 * This is where a carrier finds out *which* freight they are losing, rather than
 * only that they are losing some — the actionable half of the whole Performance
 * page. Each shipment shows their own rank on the board the customer saw; none
 * shows a price or a premium, and no other carrier is named.
 */
export function LaneDrillDownDialog({
  lane,
  fromDate,
  toDate,
  open,
  onOpenChange,
}: LaneDrillDownDialogProps) {
  const [detail, setDetail] = useState<PerformanceLaneDetail | undefined>(
    undefined,
  );
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (open !== true) return;
    let cancelled = false;
    setFailed(false);
    carriersApi()
      .getCarrierPerformanceLane({
        fromDate,
        toDate,
        pickup: lane.pickup,
        delivery: lane.delivery,
      })
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, attempt, fromDate, toDate, lane.pickup, lane.delivery]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`${lane.pickup} → ${lane.delivery}`}
      description="Every shipment you priced on this lane, and where you landed."
      width="wide"
    >
      <Stack gap={4}>
        <Panel title="This Lane">
          <Grid columns={4} gap="md">
            <PerformanceMetric
              label="You Priced"
              value={lane.quoted.toLocaleString()}
              size="xl"
              explanation="Distinct shipments on this lane that you returned a customer-visible quote for, in the selected window. Re-quotes of the same real shipment count once."
            />
            <PerformanceMetric
              label="You Won"
              value={formatPercent(lane.winRate)}
              hint={`of ${lane.quoted.toLocaleString()} you priced`}
              size="xl"
              explanation="Of the shipments you priced on this lane, the share the customer booked with you. Every shipment you quoted counts, including ones nobody booked — the same rule as the headline Win Rate."
            />
            <PerformanceMetric
              label="Average Rank"
              value={lane.averagePriceRank?.toFixed(1) ?? "—"}
              size="xl"
              explanation="Your average position on this lane's quote boards, where 1 is cheapest. Carriers tied on price share a rank."
            />
            <PerformanceMetric
              label="Lane Volume"
              value={lane.laneOpportunityShipments?.toLocaleString() ?? "—"}
              hint="everything we quote here"
              size="xl"
              explanation="Every distinct shipment quoted on this lane by any carrier in the window — the size of the opportunity, not your share of it."
            />
          </Grid>
        </Panel>

        {failed && (
          <CarrierLoadFailed
            what="the detail for this lane"
            onRetry={() => setAttempt(attempt + 1)}
          />
        )}
        {detail && (
          <>
            <MonthPanel_ rows={detail.byMonth} />
            <ShipmentPanel_ rows={detail.shipments} />
          </>
        )}
      </Stack>
    </Dialog>
  );
}

function MonthPanel_({ rows }: { rows: PerformanceLaneMonthRow[] }) {
  const columns: Column<PerformanceLaneMonthRow>[] = [
    {
      key: "month",
      header: "Month",
      render: (row) => <TextCell value={row.month} />,
    },
    {
      key: "quoted",
      header: "You Priced",
      align: "right",
      render: (row) => <TextCell value={row.quoted.toLocaleString()} />,
    },
    {
      key: "won",
      header: "You Won",
      align: "right",
      render: (row) => <TextCell value={row.won.toLocaleString()} />,
    },
    {
      key: "winRate",
      header: "Win Rate",
      align: "right",
      render: (row) => (
        <TextCell
          value={row.winRate === undefined ? "—" : formatPercent(row.winRate)}
        />
      ),
    },
  ];

  return (
    <Panel title="Month by Month" fullBleed>
      <Table
        columns={columns}
        data={rows}
        rowKey={(row) => row.month}
        emptyMessage="Nothing quoted on this lane in the period."
      />
    </Panel>
  );
}

function ShipmentPanel_({ rows }: { rows: PerformanceLaneShipment[] }) {
  const columns: Column<PerformanceLaneShipment>[] = [
    {
      key: "quotedAt",
      header: "Quoted",
      render: (row) => <TextCell value={row.quotedAt} />,
    },
    {
      key: "outcome",
      header: "Outcome",
      render: (row) => (
        <Badge
          label={row.won ? "You won it" : "Lost"}
          variant={row.won ? "success" : "neutral"}
        />
      ),
    },
    {
      key: "rank",
      header: "Your Rank",
      align: "right",
      render: (row) => (
        <TextCell
          value={
            row.priceRank === undefined
              ? "—"
              : row.carriersQuoting === undefined
                ? `${row.priceRank}`
                : `${row.priceRank} of ${row.carriersQuoting}`
          }
        />
      ),
    },
    {
      key: "weight",
      header: "Weight",
      align: "right",
      render: (row) => (
        <TextCell
          value={
            row.weightPounds === undefined
              ? undefined
              : `${row.weightPounds.toLocaleString()} lb`
          }
        />
      ),
    },
    {
      key: "class",
      header: "Class",
      align: "right",
      render: (row) => <TextCell value={row.freightClass} />,
    },
    {
      key: "service",
      header: "Service",
      render: (row) => <TextCell value={row.service} />,
    },
    {
      key: "paid",
      header: "Paid to You",
      align: "right",
      render: (row) => (
        <TextCell
          value={row.spend === undefined ? undefined : formatMoney(row.spend)}
        />
      ),
    },
  ];

  return (
    <Panel
      title="Shipments"
      description="Your rank is your position on the board the customer saw. We don't show other carriers' prices."
      count={rows.length}
      fullBleed
    >
      <Table
        columns={columns}
        data={rows}
        rowKey={(row) =>
          `${row.quotedAt}-${row.priceRank ?? ""}-${row.weightPounds ?? ""}`
        }
        emptyMessage="No shipments quoted on this lane in the period."
        pageSize={25}
      />
    </Panel>
  );
}
